/*
  MOMANI — behaviour, no library, no tweening.
  1. Header state: overlay over a hero, solid on scroll, hidden on scroll-down
     past 1.4 viewports, revealed on scroll-up or when a panel opens.
  2. Panel hover intent: desktop panels open on hover after 120ms, close when
     the pointer leaves the header; click/Enter keeps working (details).
  3. Persistent buy bar on mobile after the in-page control leaves the viewport.
  4. Reserve space for late product recommendations (CLS).
  5. Image reveal, once, opacity only.
  6. Mobile gallery counter on the PDP; footer localisation selects submit on change.
  0. Input modality class for focus rings; sideways rows with text arrows.
*/
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* 0. input modality: focus rings show for keyboard users only */
  const root = document.documentElement;
  root.classList.add('is-pointer');
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Tab' || e.key === 'Enter' || e.key === ' ' || e.key === 'Escape' || e.key.startsWith('Arrow')) { root.classList.add('is-keyboard'); root.classList.remove('is-pointer'); }
  }, true);
  window.addEventListener('pointerdown', () => { root.classList.remove('is-keyboard'); root.classList.add('is-pointer'); }, true);

  /* 0b. rows that scroll sideways (You may also like): text arrows move one card */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-momani-scroll]');
    if (!btn) return;
    const row = btn.closest('.momani-related')?.querySelector('.momani-grid--related');
    if (!row) return;
    const card = row.querySelector('li');
    const step = card ? card.getBoundingClientRect().width + 4 : row.clientWidth / 4;
    row.scrollBy({ left: Number(btn.dataset.momaniScroll) * step, behavior: reduce.matches ? 'auto' : 'smooth' });
  });
  const armRows = () => {
    document.querySelectorAll('.momani-related').forEach((rel) => {
      const row = rel.querySelector('.momani-grid--related');
      const nav = rel.querySelector('.momani-related__nav');
      if (!row || !nav) return;
      nav.hidden = row.scrollWidth <= row.clientWidth + 4;
    });
  };
  document.querySelectorAll('product-recommendations').forEach((el) => new MutationObserver(armRows).observe(el, { childList: true, subtree: true }));
  window.addEventListener('resize', armRows, { passive: true });
  armRows();
  const shell = document.querySelector('[data-momani-shell]');

  /* 1. header state */
  if (shell) {
    const heroEl = document.querySelector('main [data-momani-hero]');
    const firstSection = document.querySelector('main .shopify-section');
    const hero = heroEl && heroEl.closest('.shopify-section') === firstSection ? heroEl : null;
    if (hero && hero.classList.contains('momani-campaign--overlay')) shell.classList.add('momani-shell--overlay');
    if (!hero) shell.classList.remove('momani-shell--overlay');
    if (hero && hero.classList.contains('momani-campaign--reversed')) shell.classList.add('momani-shell--reversed');
    const HIDE_AFTER = () => window.innerHeight * 1.4;
    const DELTA = 6;
    let ticking = false;
    let last = window.scrollY;
    const panelOpen = () => !!shell.querySelector('details[open]');
    const sync = () => {
      ticking = false;
      const y = Math.max(0, window.scrollY);
      shell.classList.toggle('is-scrolled', y > 8);
      const moved = y - last;
      if (Math.abs(moved) > DELTA) {
        if (panelOpen() || y < HIDE_AFTER()) shell.classList.remove('is-hidden');
        else shell.classList.toggle('is-hidden', moved > 0);
        last = y;
      }
    };
    const onScroll = () => { if (ticking) return; ticking = true; requestAnimationFrame(sync); };
    sync();
    window.addEventListener('scroll', onScroll, { passive: true });
    shell.addEventListener('toggle', (e) => { if (e.target instanceof HTMLDetailsElement && e.target.open) shell.classList.remove('is-hidden'); }, true);
    shell.addEventListener('focusin', () => shell.classList.remove('is-hidden'));

    /* 2. panels and the search sheet: one open at a time; hover intent on desktop */
    const items = shell.querySelectorAll('.momani-nav__item > details-disclosure > details, .momani-nav__item > details');
    const searchModal = shell.querySelector('details-modal.momani-search');
    const searchDetails = searchModal ? searchModal.querySelector('details') : null;
    const closeSearch = () => { if (searchModal && searchDetails && searchDetails.open) { if (typeof searchModal.close === 'function') searchModal.close(false); else searchDetails.open = false; } };
    const closeAll = (except) => { items.forEach((d) => { if (d !== except && d.open) d.open = false; }); if (except !== searchDetails) closeSearch(); };
    // A sheet is revealed only when none was open; switching from one sheet to
    // another swaps the content in place (the shell carries is-switching until
    // every sheet has closed).
    const sheetsOpen = () => [...items].some((x) => x.open) || !!(searchDetails && searchDetails.open);
    const setSwitching = (on) => shell.classList.toggle('is-switching', on);
    if (searchDetails) searchDetails.addEventListener('toggle', () => { if (searchDetails.open) { if ([...items].some((x) => x.open)) setSwitching(true); closeAll(searchDetails); } else if (!sheetsOpen()) setSwitching(false); });
    items.forEach((d) => { d.addEventListener('toggle', () => { if (d.open) closeAll(d); else if (!sheetsOpen()) setSwitching(false); }); });
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    if (fine.matches && items.length) {
      let timer = null;
      items.forEach((d) => {
        const li = d.closest('.momani-nav__item');
        const summary = d.querySelector('summary');
        if (summary) summary.addEventListener('click', (e) => { if (d.open) e.preventDefault(); });
        li.addEventListener('pointerenter', () => { clearTimeout(timer); timer = setTimeout(() => { if (!d.open) { if (sheetsOpen()) setSwitching(true); closeAll(d); d.open = true; } }, 120); });
        li.addEventListener('pointerleave', () => { clearTimeout(timer); });
      });
      shell.querySelector('.momani-nav').addEventListener('pointerleave', () => { clearTimeout(timer); timer = setTimeout(() => items.forEach((d) => { if (d.open) d.open = false; }), 260); });
      shell.querySelector('.momani-nav').addEventListener('pointerenter', () => clearTimeout(timer));
      // The frames in the sheets are lazy images; on a desktop they are fetched
      // once the page is idle, so a menu never opens onto empty plates.
      const warm = () => shell.querySelectorAll('.momani-panel img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 3000 }); else setTimeout(warm, 2000);
      const dim = shell.querySelector('.momani-shell__dim');
      if (dim) dim.addEventListener('click', () => closeAll(null));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') items.forEach((d) => { if (d.open) d.open = false; }); });
    }
  }

  /* 3. persistent buy bar */
  const bar = document.querySelector('[data-momani-buy-bar]');
  const anchor = document.querySelector('[data-momani-buy-anchor]');
  if (bar && anchor) {
    bar.classList.add('momani-buy-bar--managed');
    // The bar shows once the in-page control has scrolled above the viewport.
    // A scroll listener rather than an observer alone: an instant jump past the
    // anchor never intersects it, so the observer would never fire.
    let barTick = false;
    const syncBar = () => { barTick = false; bar.classList.toggle('is-visible', anchor.getBoundingClientRect().bottom < 0); };
    window.addEventListener('scroll', () => { if (barTick) return; barTick = true; requestAnimationFrame(syncBar); }, { passive: true });
    syncBar();
  }

  /* 4. reserve recommendations space: the head, one row of 4:5 cards at the
     product gutter and the module padding, so the late row does not move the
     footer (CLS) */
  document.querySelectorAll('product-recommendations').forEach((el) => {
    if (el.querySelector('ul')) return;
    const cs = getComputedStyle(el);
    const inner = el.clientWidth - parseFloat(cs.paddingLeft || 0) - parseFloat(cs.paddingRight || 0);
    const cols = window.innerWidth < 750 ? 2 : 4;
    const card = (inner - (cols - 1) * 4) / cols;
    const reserve = Math.round(parseFloat(cs.paddingTop || 0) + parseFloat(cs.paddingBottom || 0) + 44 + card * 1.25 + 52);
    el.style.minHeight = `${reserve}px`;
    const release = () => { el.style.minHeight = ''; };
    const mo = new MutationObserver(() => { release(); mo.disconnect(); });
    mo.observe(el, { childList: true, subtree: true });
    setTimeout(() => { release(); mo.disconnect(); }, 6000);
  });

  /* 5. image reveal */
  const reveal = () => {
    const targets = document.querySelectorAll('[data-momani-reveal]:not(.is-revealed)');
    if (!targets.length) return;
    if (reduce.matches) { targets.forEach((t) => t.classList.add('is-revealed')); return; }
    const io = new IntersectionObserver((entries, observer) => {
      entries.forEach((e) => { if (!e.isIntersecting) return; e.target.classList.add('is-revealed'); observer.unobserve(e.target); });
    }, { rootMargin: '0px 0px -8% 0px' });
    targets.forEach((t) => io.observe(t));
  };
  reveal();
  document.addEventListener('shopify:section:load', reveal);

  /* 6a. a native select sizes to its longest option; size the sort control to
     the value it shows. Measured on a canvas: no DOM insertion, so nothing
     here can feed a mutation observer. */
  const measure = document.createElement('canvas').getContext('2d');
  const fitSelects = () => {
    document.querySelectorAll('select.facet-filters__sort, select.momani-select').forEach((sel) => {
      const opt = sel.options[sel.selectedIndex];
      if (!opt || !measure) return;
      const cs = getComputedStyle(sel);
      measure.font = cs.font;
      const w = measure.measureText(opt.textContent.trim()).width;
      sel.style.width = `${Math.ceil(w) + parseFloat(cs.paddingRight || 0) + parseFloat(cs.paddingLeft || 0) + 2}px`;
    });
  };
  fitSelects();
  document.addEventListener('change', (e) => { if (e.target.matches('select')) fitSelects(); });
  document.addEventListener('shopify:section:load', fitSelects);

  /* 7. the mobile menu's groups are accordions, not Dawn's slide-in submenus.
     Dawn's HeaderDrawer binds every <summary> inside the drawer and, for a
     nested one, moves the focus trap into that sub-panel and focuses a Back
     button that does not exist here: an uncaught TypeError on every group
     open, and Tab could no longer reach Close or the other groups. The click
     is stopped in the capture phase before it reaches Dawn's handler; the
     <details> toggles natively and the drawer keeps one trap. Escape and the
     drawer's own close path are untouched. */
  document.addEventListener('click', (e) => {
    if (e.target.closest('.momani-mobile__summary')) e.stopPropagation();
  }, true);

  /* 6c. pictures fade in as they arrive instead of popping in. Only images
     that are not loaded yet are touched; without this script they simply
     appear, so nothing depends on it. */
  const fadeIn = (root) => {
    root.querySelectorAll('.momani-card__media img, .momani-pair__media img, .momani-panel__tile-media img, .momani-campaign__media img, .momani-search__card-media img, .momani-carousel img').forEach((img) => {
      if (img.complete || img.dataset.momaniFade) return;
      img.dataset.momaniFade = '1';
      img.classList.add('is-arriving');
      const done = () => img.classList.remove('is-arriving');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  };
  fadeIn(document);
  document.addEventListener('shopify:section:load', (e) => fadeIn(e.target));

  /* 6b. footer country and language: a native select that applies on change */
  document.querySelectorAll("form[data-momani-localization] select").forEach((sel) => {
    sel.addEventListener("change", () => sel.form.submit());
  });

  /* 6. mobile gallery counter */
  const gallery = document.querySelector('.momani-pdp__gallery');
  const counter = document.querySelector('[data-momani-gallery-counter]');
  if (gallery && counter) {
    const list = gallery.querySelector('.product__media-list');
    const items = list ? [...list.querySelectorAll('.product__media-item')] : [];
    if (list && items.length > 1) {
      const update = () => {
        const i = Math.round(list.scrollLeft / list.clientWidth) + 1;
        counter.textContent = `${Math.min(items.length, Math.max(1, i))} / ${items.length}`;
      };
      list.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      update();
    }
  }
})();
